import React, { useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Check } from "@phosphor-icons/react";
import { useLang } from "@/lib/LanguageContext";
import { wa } from "@/lib/pfd";
import { LANDINGS } from "@/lib/pfdLanding";
import { LANDINGS_PARIS_1 } from "@/lib/pfdLandingParis1";
import { LANDINGS_PARIS_2 } from "@/lib/pfdLandingParis2";
import { LANDINGS_CLM } from "@/lib/pfdLandingCLM";
import { LANDINGS_FAMILIES } from "@/lib/pfdLandingFamilies";
import { LANDINGS_COUNTRIES } from "@/lib/pfdLandingCountries";
import { LANDINGS_FASHION_WEEK } from "@/lib/pfdLandingFashionWeek";
import { card, landingImage, articleImage } from "@/lib/images";
import { getArticle } from "@/lib/articles";

const READING = {
  fashion: ["what-to-delegate-personal-assistant-paris", "discretion-standard-ndas-privacy", "personal-assistant-paris-cost-prices"],
  executive: ["executive-support-international-leaders", "what-to-delegate-personal-assistant-paris", "setting-up-business-france-administrative-reality"],
  families: ["paris-international-families", "first-30-days-france", "french-administration-decoded"],
  corporate: ["executive-support-international-leaders", "setting-up-business-france-administrative-reality", "discretion-standard-ndas-privacy"],
  experiences: ["luxury-experiences-paris-international-visitors", "private-concierge-vs-hotel-concierge-paris", "last-minute-restaurant-reservations-paris"],
  businessTravel: ["business-travel-concierge-paris-how-it-works", "paris-airports-cdg-orly-le-bourget-guide", "executive-support-international-leaders"],
  lastMinute: ["24-hours-in-paris-what-to-delegate", "last-minute-restaurant-reservations-paris", "private-concierge-cost-paris"],
  chauffeur: ["paris-airports-cdg-orly-le-bourget-guide", "private-concierge-vs-hotel-concierge-paris", "what-to-delegate-personal-assistant-paris"],
  concierge: ["private-concierge-cost-paris", "private-concierge-vs-hotel-concierge-paris", "last-minute-restaurant-reservations-paris"],
  restaurant: ["last-minute-restaurant-reservations-paris", "private-concierge-vs-hotel-concierge-paris", "private-concierge-cost-paris"],
  shopping: ["private-shopping-paris-how-it-works", "private-concierge-cost-paris", "private-assistant-vs-concierge"],
  default: ["hire-private-assistant-france-guide", "private-assistant-vs-concierge", "personal-assistant-paris-cost-prices"],
};
const readingFor = (slug) => {
  if (slug.includes("fashion-week")) return READING.fashion;
  if (slug.includes("experiences")) return READING.experiences;
  if (slug.includes("business-travel")) return READING.businessTravel;
  if (slug.includes("last-minute")) return READING.lastMinute;
  if (slug.includes("chauffeur") || slug.includes("airport-transfer")) return READING.chauffeur;
  if (slug.includes("restaurant")) return READING.restaurant;
  if (slug.includes("shopper")) return READING.shopping;
  if (slug.includes("executive") || slug.includes("business")) return READING.executive;
  if (slug.includes("families")) return READING.families;
  if (slug.includes("corporate")) return READING.corporate;
  if (slug.includes("concierge")) return READING.concierge;
  return READING.default;
};
const CTA = { en: "Tell us what you need in Paris.", fr: "Dites-nous ce dont vous avez besoin à Paris." };
import PageShell from "@/components/PageShell";
import ImageHero from "@/components/ImageHero";
import ImageCard from "@/components/ImageCard";
import Seo, { faqSchema, serviceSchema, breadcrumbSchema } from "@/components/Seo";
import { LANDINGS_LIFESTYLE } from "@/lib/pfdLandingLifestyle";
import { DEPTH } from "@/lib/pfdLandingDepth";
import { LANDINGS_CHAUFFEUR, CHAUFFEUR_HUB } from "@/lib/pfdLandingChauffeur";
import { LANDINGS_WAVE2 } from "@/lib/pfdLandingWave2";

const ALL_LANDINGS = { ...LANDINGS, ...LANDINGS_PARIS_1, ...LANDINGS_PARIS_2, ...LANDINGS_CLM, ...LANDINGS_FAMILIES, ...LANDINGS_COUNTRIES, ...LANDINGS_FASHION_WEEK, ...LANDINGS_LIFESTYLE, ...LANDINGS_CHAUFFEUR, ...LANDINGS_WAVE2 };

export default function Landing({ slug }) {
  const { t, lang } = useLang();
  const content = ALL_LANDINGS[slug];
  const p = content ? content[lang] : null;

  const crumbs = useMemo(
    () =>
      p
        ? [
            { label: lang === "fr" ? "Accueil" : "Home", to: "/" },
            { label: "Services", to: "/services" },
            { label: `${p.h1a} ${p.h1b}`.replace(/\s+/g, " ").replace(/[.,]$/, "").trim(), to: `/${slug}` },
          ]
        : [],
    [p, lang, slug]
  );

  const schema = useMemo(() => {
    if (!p) return null;
    return [
      serviceSchema({ name: p.h1a + " " + p.h1b, description: p.seoDesc, path: `/${slug}` }),
      faqSchema(p.faqs),
      breadcrumbSchema(crumbs),
    ];
  }, [p, slug, crumbs]);

  if (!content) return <Navigate to="/" replace />;

  const img = landingImage(slug);
  const isFW = slug.includes("fashion-week");
  const sections = p.sections || (DEPTH[slug] && DEPTH[slug][lang]);
  const related = slug === "paris-fashion-week/private-driver" ? [...(p.related || []), ...CHAUFFEUR_HUB.filter((h) => !h.slug.includes("fashion-week")).map((h) => ({ to: `/${h.slug}`, label: h[lang] }))] : p.related;

  return (
    <PageShell>
      <Seo title={p.seoTitle} description={p.seoDesc} schema={schema} />

      {/* Hero — Answer first (Minto) */}
      <ImageHero testId="landing-hero" image={img} eyebrow={p.eyebrow} titleA={p.h1a} titleB={p.h1b} crumbs={crumbs} tall>
        <Link
          data-testid="landing-cta-contact"
          to="/contact"
          className="inline-flex items-center justify-center gap-3 bg-stone-50 text-slate-900 px-8 py-4 text-sm font-medium hover:bg-stone-200 transition-colors"
        >
          {CTA[lang]}
          <ArrowRight size={16} />
        </Link>
        <a
          data-testid="landing-cta-whatsapp"
          href={wa(p.waMsg)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-3 border border-stone-50/40 text-stone-50 px-8 py-4 text-sm font-medium hover:border-stone-50 transition-colors"
        >
          WhatsApp
        </a>
      </ImageHero>

      <section data-testid="landing-answer-section" className="py-14 sm:py-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <p data-testid="landing-answer" className="text-stone-800 font-serif text-lg sm:text-2xl lg:text-3xl leading-relaxed sm:leading-snug max-w-4xl border-l-2 border-stone-900 pl-5 sm:pl-8">
            {p.answer}
          </p>
        </div>
      </section>

      {/* SCQ */}
      <section data-testid="landing-scqa" className="bg-stone-100 border-y border-stone-200 py-14 sm:py-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
          <p className="text-stone-600 leading-relaxed">{p.s}</p>
          <p className="text-stone-600 leading-relaxed">{p.c}</p>
          <p className="font-serif text-2xl text-stone-900 leading-snug italic">{p.q}</p>
        </div>
      </section>

      {/* Deliverables */}
      <section data-testid="landing-deliverables" className="py-16 sm:py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <h2 className="font-serif text-base md:text-lg text-stone-900">{p.deliverablesTitle}</h2>
            <div className="mt-8 overflow-hidden aspect-[4/5] bg-stone-200 hidden lg:block">
              <img src={card(img === "executiveDesk" ? "grandInterior" : "executiveDesk")} alt={p.deliverablesTitle} loading="lazy" className="w-full h-full object-cover" />
            </div>
          </div>
          <ul className="lg:col-span-8 space-y-5">
            {p.deliverables.map((d) => (
              <li key={d} className="flex items-start gap-4 border-b border-stone-200 pb-5">
                <Check size={18} className="text-stone-900 mt-1 shrink-0" />
                <span className="text-stone-700 leading-relaxed">{d}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* In practice — prose sections (depth) */}
      {sections && (
        <section data-testid="landing-sections" className="border-t border-stone-200 py-16 sm:py-28 px-6 lg:px-10">
          <div className="max-w-3xl mx-auto space-y-14">
            {sections.map((s) => (
              <div key={s.h2}>
                <h2 className="font-serif text-2xl sm:text-3xl text-stone-900 leading-snug">{s.h2}</h2>
                <div className="mt-5 space-y-5">
                  {s.paras.map((para) => (
                    <p key={para.slice(0, 40)} className="text-stone-600 leading-relaxed">{para}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* For who */}
      <section data-testid="landing-forwho" className="bg-slate-900 text-stone-200 py-16 sm:py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-serif text-base md:text-lg text-stone-50">{p.forTitle}</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10">
            {p.forWho.map((w, i) => (
              <div key={w} className="border-t border-slate-700 pt-6">
                <span className="font-serif text-3xl text-stone-500">0{i + 1}</span>
                <p className="mt-4 text-stone-300 leading-relaxed">{w}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {related && (
        <section data-testid="landing-related" className="bg-stone-100 border-y border-stone-200 py-20 px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <p className="eyebrow">{isFW ? (lang === "fr" ? "Explorer la Fashion Week" : "Explore Fashion Week") : lang === "fr" ? "Services associés" : "Related services"}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {related.map((r) => (
                <Link key={r.to} to={r.to} data-testid={`related-link-${r.to.split("/").pop()}`} className="border border-stone-300 hover:border-stone-900 hover:bg-stone-50 px-5 py-3 text-sm text-stone-800 transition-colors">
                  {r.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related briefing (country pages) */}
      {p.article && getArticle(p.article) && (
        <section data-testid="landing-briefing" className="bg-stone-100 border-y border-stone-200 py-16 sm:py-28 px-6 lg:px-10">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <p className="eyebrow">{t.journal.eyebrow}</p>
              <h2 className="font-serif mt-6 text-3xl sm:text-4xl text-stone-900 leading-tight">{getArticle(p.article)[lang].title}</h2>
              <p className="mt-5 text-stone-600 leading-relaxed">{getArticle(p.article)[lang].abstract}</p>
            </div>
            <div className="lg:col-span-5 lg:col-start-8">
              <ImageCard
                to={`/journal/${p.article}`}
                testId="landing-article-link"
                image={articleImage(p.article)}
                eyebrow={getArticle(p.article)[lang].category}
                title={lang === "fr" ? "Lire le briefing" : "Read the briefing"}
                cta={lang === "fr" ? "Lire" : "Read"}
                aspect="aspect-[4/3]"
              />
            </div>
          </div>
        </section>
      )}

      {/* Related reading (linking engine) */}
      <section data-testid="landing-reading" className="py-14 sm:py-24 px-6 lg:px-10 border-t border-stone-200">
        <div className="max-w-7xl mx-auto">
          <p className="eyebrow">{lang === "fr" ? "Lecture associée" : "Related reading"}</p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8">
            {readingFor(slug).filter((r) => r !== p.article).map(getArticle).filter(Boolean).map((r) => (
              <ImageCard key={r.slug} to={`/journal/${r.slug}`} testId={`reading-${r.slug}`} image={articleImage(r.slug)} eyebrow={r[lang].category} title={r[lang].title} cta={lang === "fr" ? "Lire" : "Read"} aspect="aspect-[4/3]" />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="landing-faq" className="py-16 sm:py-32 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto">
          <p className="eyebrow">FAQ</p>
          <div className="mt-10 space-y-10">
            {p.faqs.map((f) => (
              <div key={f.q} className="border-b border-stone-200 pb-8">
                <h3 className="font-medium text-stone-900 text-lg">{f.q}</h3>
                <p className="mt-3 text-stone-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="landing-final-cta" className="bg-stone-100 border-t border-stone-200 py-16 sm:py-32 px-6 lg:px-10 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-4xl sm:text-5xl font-light text-stone-900">{t.finalCta.title}</h2>
          <p className="mt-6 text-stone-600 text-lg">{t.finalCta.text}</p>
          <Link
            data-testid="landing-final-contact"
            to="/contact"
            className="mt-10 inline-flex items-center justify-center gap-3 bg-slate-900 text-stone-50 px-8 py-4 text-sm font-medium hover:bg-slate-800 transition-colors"
          >
            {t.finalCta.cta}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
